import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Upload, X, Loader2, Image as ImageIcon, Video } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// folderPath -> bucket name
const BUCKET_MAP = {
  logos: 'brand-logos',
  thumbnails: 'photo-thumbnails',
  'video-previews': 'video-previews',
  templates: 'templates',
};

const sanitizeFileName = (name) => {
  const ext = name.includes('.') ? '.' + name.split('.').pop() : '';
  const base = name.replace(/\.[^.]+$/, '');
  const safe = base.replace(/[^a-zA-Z0-9_-]+/g, '_').slice(0, 60) || 'file';
  return `${safe}${ext.toLowerCase()}`;
};

const generateKey = (originalName) => {
  const safe = sanitizeFileName(originalName);
  const stamp = Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  return `${safe.replace(/(\.[^.]+)$/, `_${stamp}$1`)}`;
};

const R2Upload = ({
  label,
  name,
  value,
  onChange,
  acceptedFormats = 'all', // 'images', 'videos', or 'all'
  folderPath = '',
  placeholder = 'Enter URL or click Upload',
  className = ''
}) => {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [bucketsConfig, setBucketsConfig] = useState({});
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const r = await axios.get(`${API}/r2/config`);
        setBucketsConfig(r.data?.buckets || {});
      } catch (e) {
        console.error('Failed to load R2 config:', e);
      }
    };
    fetchConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAccept = () => {
    if (acceptedFormats === 'images') return 'image/jpeg,image/png,image/webp,image/svg+xml';
    if (acceptedFormats === 'videos') return 'video/mp4,video/webm,video/quicktime';
    return '*/*';
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const bucket = BUCKET_MAP[folderPath];
    if (!bucket) {
      toast.error(`Unknown folder: ${folderPath}`);
      return;
    }

    setUploading(true);
    try {
      const key = generateKey(file.name);

      // 1) Get presigned PUT URL
      const presign = await axios.post(
        `${API}/r2/presign-upload`,
        { bucket, key, contentType: file.type || 'application/octet-stream' },
        { withCredentials: true }
      );
      const { uploadUrl, publicUrl } = presign.data;

      // 2) Upload directly to R2
      await axios.put(uploadUrl, file, {
        headers: { 'Content-Type': file.type || 'application/octet-stream' }
      });

      // 3) Push final public URL to parent form
      onChange({ target: { name: name || inputRef.current?.name || '', value: publicUrl } });
      toast.success('Uploaded');
    } catch (error) {
      console.error('Upload error:', error);
      const detail = error.response?.data?.detail || error.message;
      toast.error(`Upload failed: ${detail}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const isR2Url = (url) => {
    if (!url) return false;
    return Object.values(bucketsConfig).some((base) => url.startsWith((base || '').replace(/\/$/, '') + '/'));
  };

  const handleDelete = async () => {
    const currentUrl = (value || '').trim();
    const remoteDeletable = currentUrl && isR2Url(currentUrl);

    if (!currentUrl) return;

    const msg = remoteDeletable
      ? 'Delete this file from R2 and clear the field?'
      : 'Clear this URL?';
    if (!window.confirm(msg)) return;

    if (!remoteDeletable) {
      onChange({ target: { name: name || inputRef.current?.name || '', value: '' } });
      toast.success('URL cleared');
      return;
    }

    setDeleting(true);
    try {
      await axios.post(`${API}/r2/delete`, { url: currentUrl }, { withCredentials: true });
      toast.success('File deleted');
    } catch (error) {
      console.error('Delete error:', error);
      toast.warning('Could not delete from R2; field was cleared.');
    } finally {
      onChange({ target: { name: name || inputRef.current?.name || '', value: '' } });
      setDeleting(false);
    }
  };

  const isImage = value && /\.(jpe?g|png|gif|webp|svg)$/i.test(value);
  const isVideo = value && /\.(mp4|webm|mov|quicktime)$/i.test(value);

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      )}
      <div className="flex flex-col sm:flex-row gap-2">
        <Input
          ref={inputRef}
          type="text"
          name={name}
          value={value || ''}
          onChange={onChange}
          placeholder={placeholder}
          className="flex-1"
        />
        <input
          ref={fileInputRef}
          type="file"
          accept={getAccept()}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
          {value && (
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
              title={isR2Url(value) ? 'Delete from R2' : 'Clear URL'}
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
            </Button>
          )}
        </div>
      </div>

      {value && (
        <div className="mt-2">
          {isImage ? (
            <img
              src={value}
              alt="Preview"
              className="max-h-32 rounded border border-gray-200 object-contain bg-gray-50"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : isVideo ? (
            <video
              src={value}
              className="max-h-32 rounded border border-gray-200 bg-gray-50"
              controls
              muted
              preload="metadata"
            />
          ) : (
            <div className="text-xs text-gray-500 flex items-center gap-1">
              {acceptedFormats === 'images' ? <ImageIcon className="w-3 h-3" /> : <Video className="w-3 h-3" />}
              <span className="truncate">{value}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default R2Upload;
